import type { ReactElement } from "react";
import { Link, useLocation } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { loadFailureMessage } from "../../components/load-failure.js";
import { FormSummary } from "../../forms/fields.js";
import { useApiForm, type ApiForm } from "../../forms/use-api-form.js";

import { accountApi, accountUrls } from "./urls.js";

/** 自分の状態（<c>GET /api/account</c>。サーバの AccountIndexViewModel と同じ欄）。 */
export interface AccountSettings {
    readonly email: string;
    readonly newEmail: string;
    readonly isChangeEmailProcessing: boolean;
    /** 期限（24 時間）が切れた申請の新しいアドレス。無ければ空。期限切れは申請中に数えない */
    readonly expiredNewEmail: string;
    readonly isChangeEmailExpired: boolean;
    readonly isTwoFactorEnabled: boolean;
}

/**
 * メールアドレス。申請中なら取り消し、そうでなければ変更の申請。
 * <b>期限切れの申請は申請中に数えない</b>（リンクが使えない）。変更の欄の上に、もう一度申請する案内を添える。
 */
function EmailSection({ settings, change, cancel }: {
    readonly settings: AccountSettings;
    readonly change: ApiForm;
    readonly cancel: ApiForm;
}): ReactElement {
    return (
        <section className="card mb-4 p-6">
            <h2 className="section-title">メールアドレス</h2>

            {settings.isChangeEmailProcessing
                ? (
                    <>
                        <div className="alert-warning mb-4">
                            <span className="font-medium">{settings.newEmail}</span> への変更を申請中です。<br />
                            新しいアドレス宛の確認メールから手続きを完了してください。
                        </div>
                        <form method="post" noValidate onSubmit={cancel.onSubmit}>
                            <button type="submit" className="btn-secondary btn-sm" disabled={cancel.busy}>変更を取り消す</button>
                        </form>
                    </>
                )
                : (
                    <>
                        {settings.isChangeEmailExpired
                            ? (
                                <div className="alert-info mb-4" data-change-email-expired="">
                                    <span className="font-medium">{settings.expiredNewEmail}</span> への変更の申請は、有効期限（24 時間）が過ぎたため無効になりました。<br />
                                    変更する場合は、もう一度申請してください。
                                </div>
                            )
                            : null}
                        <p className="mb-5 text-slate-900">{settings.email}</p>
                        <form method="post" noValidate onSubmit={change.onSubmit} className="flex flex-wrap items-end gap-3">
                            <div className="min-w-0 grow">
                                <label className="form-label" htmlFor="newEmail">新しいメールアドレス</label>
                                <input
                                    className="form-input" id="newEmail" name="newEmail" type="email"
                                    placeholder="mail@example.com" required
                                />
                            </div>
                            <button type="submit" className="btn-primary" disabled={change.busy}>変更する</button>
                        </form>
                    </>
                )}
        </section>
    );
}

/** 二段階認証。<b>解除は確かめる画面を挟む</b>——押すだけで解除できると、席を離れた端末から付け替えられる。 */
function TwoFactorSection({ enabled }: { readonly enabled: boolean }): ReactElement {
    return (
        <section className="card mb-4 flex items-center justify-between gap-4 p-6" data-two-factor="">
            <div>
                <h2 className="section-title mb-0">二段階認証</h2>
                {enabled
                    ? (
                        <p className="page-subtitle">
                            <span className="badge-success">有効</span>
                            認証アプリの確認コードでログインします。
                        </p>
                    )
                    : <p className="page-subtitle">未設定です。次回のログイン時に設定が必要になります（今すぐ設定することもできます）。</p>}
            </div>
            {enabled
                ? <Link to={accountUrls.disableTwoFactor} className="btn-danger-quiet btn-sm shrink-0">解除する</Link>
                : <Link to={accountUrls.enableTotp} className="btn-secondary btn-sm shrink-0">設定する</Link>}
        </section>
    );
}

function AccountSettingsContent(): ReactElement {
    const data = useApiData<AccountSettings>(accountApi.settings);
    const change = useApiForm(accountApi.changeEmail);
    const cancel = useApiForm(accountApi.cancelChangeEmail);

    return (
        <div className="page max-w-3xl">
            <p className="mb-4"><Link to={accountUrls.tenants} className="btn-secondary btn-sm">契約一覧へ戻る</Link></p>
            <div className="panel">
                <h1 className="page-title mb-7">アカウント設定</h1>

                <FormSummary messages={[...change.summary, ...cancel.summary]} />

                {data.state === "loading" ? <p className="text-slate-500">読み込んでいます…</p> : null}
                {data.state === "failed"
                    ? <div className="alert-danger" role="alert">{loadFailureMessage("アカウントの情報", data)}</div>
                    : null}
                {data.state === "ready"
                    ? (
                        <>
                            <EmailSection settings={data.value} change={change} cancel={cancel} />

                            <section className="card mb-4 flex items-center justify-between gap-4 p-6">
                                <div>
                                    <h2 className="section-title mb-0">パスワード</h2>
                                    <p className="page-subtitle">定期的な変更をおすすめします。</p>
                                </div>
                                <Link to={accountUrls.changePassword} className="btn-secondary btn-sm shrink-0">変更する</Link>
                            </section>

                            <TwoFactorSection enabled={data.value.isTwoFactorEnabled} />

                            {/* 取り消せない操作なので枠で区切る。赤くするのは枠だけ（docs/01） */}
                            <section className="card-danger flex items-center justify-between gap-4 p-6">
                                <div>
                                    <h2 className="section-title mb-0">退会</h2>
                                    <p className="page-subtitle">アカウントのご利用を終了します。</p>
                                </div>
                                <Link to={accountUrls.delete} className="btn-danger-quiet btn-sm shrink-0">退会する</Link>
                            </section>
                        </>
                    )
                    : null}
            </div>
        </div>
    );
}

/**
 * アカウント設定（メール・パスワード・二段階認証・退会への入口）。
 *
 * <b>移るたびに読み直す。</b>取り消し・解除・変更の後は API が同じ画面（/Account）を返すため、
 * URL が変わらず、そのままでは古い状態が残る。移った印（location.key）で描き直す。
 */
export function AccountSettingsPage(): ReactElement {
    usePageMeta("アカウント設定");
    const { key } = useLocation();

    return <AccountSettingsContent key={key} />;
}
